import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import * as customerActionTypes from 'constants/customerSearch';
import * as loginActionTypes from 'constants/Login';
import * as persistenceTypes from 'constants/persistence';
import { ErplyLongAttributes } from 'utils';

import { createOverrideSelector } from './Plugins';

const defaultCustomer = (state = {}, { type, data }) => {
  switch (type) {
    case customerActionTypes.GET_DEFAULT_CUSTOMER.SUCCESS:
      return data;
    case loginActionTypes.TYPE_LOGOUT:
    case loginActionTypes.TYPE_LOGOUT_SOFT:
      return {};
    default:
      return state;
  }
};

const customer = (state = null, { type, data, payload }) => {
  switch (type) {
    case customerActionTypes.SET_CUSTOMER_SUCCESS:
    case customerActionTypes.SET_ONE_CUSTOMER_SUCCESS:
      return data;
    case customerActionTypes.GET_CUSTOMER_STORE_CREDIT_SUCCESS:
      return {
        ...state,
        availableCredit: payload,
      };
    case customerActionTypes.SAVE_CUSTOMER_CARD_TOKEN_INFO:
      return {
        ...state,
        longAttributes: new ErplyLongAttributes(state.longAttributes).set(
          'cardToken',
          JSON.stringify(data),
        ).asArray,
      };

    case loginActionTypes.TYPE_LOGOUT:
    case loginActionTypes.TYPE_LOGOUT_SOFT:
    case customerActionTypes.RESET_CUSTOMER:
      return null;
    default:
      return state;
  }
};

const customerGroups = (state = [], { type, data }) => {
  switch (type) {
    case customerActionTypes.GET_CUSTOMER_GROUPS_SUCCESS:
      return data;
    case loginActionTypes.TYPE_LOGOUT:
    case loginActionTypes.TYPE_LOGOUT_SOFT:
      return [];
    default:
      return state;
  }
};

const customerIssuedCoupons = (state = [], { type, data }) => {
  switch (type) {
    case customerActionTypes.SET_CUSTOMER_ISSUED_COUPONS.SUCCESS:
      return data;
    // If there was an error, then the state should not be changed to text, but rather should be set to default value aka empty array aka []
    case customerActionTypes.SET_CUSTOMER_ISSUED_COUPONS.ERROR:
    case customerActionTypes.RESET_CUSTOMER:
    case loginActionTypes.TYPE_LOGOUT:
    case loginActionTypes.TYPE_LOGOUT_SOFT:
      return [];
    default:
      return state;
  }
};
const customerRewardPoints = (state = 0, { type, data, error }) => {
  switch (type) {
    case customerActionTypes.GET_CUSTOMER_REWARD_POINTS_SUCCESS:
      return data;
    case customerActionTypes.GET_CUSTOMER_REWARD_POINTS_ERROR:
      return error;
    case customerActionTypes.RESET_CUSTOMER:
    case customerActionTypes.SET_CUSTOMER:
    case loginActionTypes.TYPE_LOGOUT:
    case loginActionTypes.TYPE_LOGOUT_SOFT:
      return 0;
    default:
      return state;
  }
};

const addressTypes = (state = [], { type, data }) => {
  switch (type) {
    case customerActionTypes.GET_ADDRESS_TYPES_SUCCESS:
      return data;
    case loginActionTypes.TYPE_LOGOUT_SOFT:
    case loginActionTypes.TYPE_LOGOUT:
      return [];
    default:
      return state;
  }
};

const personTitles = (state = [], { type, data }) => {
  switch (type) {
    case customerActionTypes.GET_PERSON_TITLES_SUCCESS:
      return data;
    case loginActionTypes.TYPE_LOGOUT_SOFT:
    case loginActionTypes.TYPE_LOGOUT:
      return [];
    default:
      return state;
  }
};

const jobTitles = (state = [], { type, data }) => {
  switch (type) {
    case customerActionTypes.GET_JOB_TITLES_SUCCESS:
      return data;
    case loginActionTypes.TYPE_LOGOUT_SOFT:
    case loginActionTypes.TYPE_LOGOUT:
      return [];
    default:
      return state;
  }
};

const loading = (state = {}, { type, payload }) => {
  switch (type) {
    case customerActionTypes.SET_CUSTOMER:
      return { ...state, customer: true };
    case customerActionTypes.SET_CUSTOMER_SUCCESS:
    case customerActionTypes.SET_CUSTOMER_ERROR:
      return { ...state, customer: false };
    case customerActionTypes.GET_CUSTOMER_REWARD_POINTS:
      return { ...state, points: true };
    case customerActionTypes.GET_CUSTOMER_REWARD_POINTS_SUCCESS:
    case customerActionTypes.GET_CUSTOMER_REWARD_POINTS_ERROR:
      return { ...state, points: false };

    case customerActionTypes.GET_CUSTOMER_STORE_CREDIT:
      return { ...state, credit: true };
    case customerActionTypes.GET_CUSTOMER_STORE_CREDIT_SUCCESS:
      return { ...state, credit: false };

    case loginActionTypes.TYPE_LOGOUT_SOFT:
    case loginActionTypes.TYPE_LOGOUT:
      return [];
    default:
      return state;
  }
};

export default combineReducers({
  defaultCustomer,
  customer,
  customerGroups,
  addressTypes,
  personTitles,
  jobTitles,
  customerIssuedCoupons,
  customerRewardPoints,
  loading,
});

const getSlice = state => state.customerSearch;

export const getIsLoadingCustomer = state =>
  Object.values(state.customerSearch.loading).some(v => v);

/**
 * @param {import('reducers').RootState} state
 * @returns {import('types/Coupon').IssuedCoupon[]}
 */
export const getCustomerIssuedCoupons = state =>
  getSlice(state).customerIssuedCoupons;

export const getSelectedCustomerID = state =>
  (getSlice(state).customer || getSlice(state).defaultCustomer).id;

export const getDefaultCustomer = state => getSlice(state).defaultCustomer;

export const getIsDefaultCustomer = state => {
  if (!getSlice(state).customer) {
    return true;
  }

  return getDefaultCustomer(state).id === getSelectedCustomerID(state);
};

export const getSelectedCustomer = state => {
  if (getIsDefaultCustomer(state)) {
    return getSlice(state).defaultCustomer;
  }
  return getSlice(state).customer;
};

export const getSearchCustomerGroups = state => getSlice(state).customerGroups;

export const getCustomerRewardPointsSelector = state =>
  Number(getSlice(state).customerRewardPoints) || 0;

export const getCustomerGroups = state => state.customerSearch.customerGroups;
export const getDefaultCustomerGroup = createSelector(
  getDefaultCustomer,
  getCustomerGroups,
  ({ groupID }, groups) =>
    groups.find(g => String(g.customerGroupID) === String(groupID)),
);
export const getCustomerGroupByID = groupID => state =>
  getCustomerGroups(state).find(group => group.clientGroupID === groupID);

export const getCustomerRegistryUrl = () =>
  localStorage.getItem(persistenceTypes.REDUX_CUSTOMER_REGISTRY_URL);

export const getCustomerRegistryToken = () =>
  localStorage.getItem(persistenceTypes.REDUX_CUSTOMER_REGISTRY_TOKEN);

export const getAddressTypes = createSelector(
  getSlice,
  slice => slice.addressTypes,
);
export const getPersonTitles = createSelector(
  getSlice,
  slice => slice.personTitles,
);

export const getJobTitles = createSelector(getSlice, slice => slice.jobTitles);

export const getSelectedCustomerAddresses = createSelector(
  getSelectedCustomer,
  selectedCustomer => selectedCustomer?.addresses,
);

// selector for all card tokens, mounted or unmounted
export const getSelectedCustomerAllCardTokenAttribute = createSelector(
  getSelectedCustomer,
  selectedCustomer => {
    const attributes = new ErplyLongAttributes(
      selectedCustomer.longAttributes,
    ).get('cardToken');

    if (attributes) {
      const allCardtokensAttribute = JSON.parse(attributes);
      return allCardtokensAttribute;
    }
    return [];
  },
);

// selector for getting the mounted card only
export const getSelectedCustomerMountedCardTokenAttribute = createSelector(
  getSelectedCustomer,
  selectedCustomer => {
    const attributes = new ErplyLongAttributes(
      selectedCustomer.longAttributes,
    ).get('cardToken');

    if (!attributes) {
      return null;
    }

    const cardTokenAttributes = JSON.parse(attributes);
    const selectedCustomerAttribute = cardTokenAttributes.find(
      attr => attr.mounted,
    );
    return selectedCustomerAttribute;
  },
);

function getUseCustomerRegistryBase(state) {
  return !!getCustomerRegistryUrl();
}

export const getUseCustomerRegistry = createOverrideSelector(
  'getUseCustomerRegistry',
  getUseCustomerRegistryBase,
);
